import { useCallback, useEffect, useRef, useState } from 'react';
import type { SearchPollPoint } from '../utils/searchFilters';
import type { SearchTimingDetails } from '../repositories/savedSearchRepository';
import { pollGapMs, shouldAdoptRun, stopwatchStart } from '../utils/searchStopwatch';
import type { SearchStopwatchMark, SearchStopwatchRun } from '../utils/searchStopwatch';

/**
 * Measure a search run from the click to the first pixels.
 *
 * Two mechanics carry the whole thing, and both are the reason this is a hook
 * rather than a pair of timestamps:
 *
 * 1. An effect runs *before* the browser paints, so stamping the clock there
 *    would report when React committed, not when the user saw anything —
 *    consistently optimistic by a frame or more under load, which is exactly
 *    when the number matters. Stamping inside a nested requestAnimationFrame
 *    puts the reading after the frame carrying the new rows was composited.
 *
 * 2. A milestone only counts once this run's own poll timeline has a point in
 *    it. `beginRun` clears that timeline, so `pollTimeline.length > 0` is the
 *    one signal that says the rows on screen belong to the run being timed and
 *    are not the previous run's still standing there.
 */

/** Run the callback after the frame containing the current commit is painted. */
function afterPaint(callback: () => void): void {
  if (typeof requestAnimationFrame !== 'function') {
    callback();
    return;
  }
  requestAnimationFrame(() => { requestAnimationFrame(callback); });
}

export interface SearchStopwatchInputs {
  /** Admin mode, on the search composer. Off, the hook does nothing. */
  enabled: boolean;
  /** Rows currently on screen; non-empty is the milestone being timed. */
  elementCount: number;
  /** True while the run is in flight. Only used to stop a run that paints nothing. */
  populating: boolean;
  pollTimeline: SearchPollPoint[];
  timingDetails: SearchTimingDetails | null;
}

export function useSearchStopwatch({
  enabled,
  elementCount,
  populating,
  pollTimeline,
  timingDetails,
}: SearchStopwatchInputs) {
  const [run, setRun] = useState<SearchStopwatchRun | null>(null);
  const [history, setHistory] = useState<SearchStopwatchRun[]>([]);
  /** The run's zero, for the display to count up from. */
  const [startedAtMs, setStartedAtMs] = useState(0);
  /** True between the click and the run ending, however it ends. */
  const [running, setRunning] = useState(false);
  const startedAtRef = useRef(0);
  const activeRef = useRef(false);
  const firstPaintTakenRef = useRef(false);
  const trustedRef = useRef(true);
  // The run under measurement, mirrored out of state: the marks land in
  // post-paint callbacks that must read and extend the run without going
  // through a state updater, which React is free to call twice.
  const runRef = useRef<SearchStopwatchRun | null>(null);
  // Every scheduled post-paint callback carries the token of the run it was
  // scheduled for, so a run superseded mid-flight cannot land its timing on
  // its replacement.
  const runTokenRef = useRef(0);

  const commit = useCallback((next: SearchStopwatchRun | null) => {
    runRef.current = next;
    setRun(next);
  }, []);

  /** Start the clock for the run this click is about to start. */
  const start = useCallback((clickTimeMs?: number) => {
    if (!enabled) return;
    startedAtRef.current = stopwatchStart(clickTimeMs, performance.now());
    activeRef.current = true;
    firstPaintTakenRef.current = false;
    trustedRef.current = typeof document === 'undefined' || document.visibilityState === 'visible';
    runTokenRef.current += 1;
    setStartedAtMs(startedAtRef.current);
    setRunning(true);
    commit({ firstPaint: null, trusted: trustedRef.current });
  }, [enabled, commit]);

  // Runs the composer did not start — opening a saved search is the common
  // one — get adopted here, so the face counts for them too.
  const populatingRef = useRef(false);
  useEffect(() => {
    const adopt = shouldAdoptRun({
      populating,
      wasPopulating: populatingRef.current,
      runActive: activeRef.current,
    });
    populatingRef.current = populating;
    if (adopt) start();
  }, [populating, start]);

  // A backgrounded tab throttles rAF to whole seconds, so any run that spent
  // time hidden is unmeasurable rather than merely slow. Mark it and say so.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') return;
      trustedRef.current = false;
      if (runRef.current) commit({ ...runRef.current, trusted: false });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [commit]);

  const buildMark = useCallback((gapMs: number, serverMs: number | null): SearchStopwatchMark => ({
    observedMs: Math.max(0, performance.now() - startedAtRef.current),
    pollGapMs: gapMs,
    serverMs,
  }), []);

  const hasPolled = pollTimeline.length > 0;
  const serverMs = timingDetails?.total_ms ?? null;

  // First results on screen — the milestone, and the end of the measurement.
  // The run carries on filling in behind it; that is time the user spends
  // reading rather than waiting, so the clock has no business counting it.
  useEffect(() => {
    if (!activeRef.current || firstPaintTakenRef.current) return;
    if (!hasPolled || elementCount === 0) return;
    firstPaintTakenRef.current = true;
    activeRef.current = false;
    setRunning(false);
    const token = runTokenRef.current;
    const gap = pollGapMs(pollTimeline);
    afterPaint(() => {
      if (token !== runTokenRef.current || !runRef.current) return;
      const painted = { ...runRef.current, firstPaint: buildMark(gap, serverMs) };
      commit(painted);
      setHistory((previous) => [...previous, painted].slice(-20));
    });
  }, [hasPolled, elementCount, pollTimeline, serverMs, buildMark, commit]);

  // A run that ends without painting anything — failed, or genuinely empty.
  // There is no wait to report, but the clock still has to stop.
  useEffect(() => {
    if (!activeRef.current || populating) return;
    activeRef.current = false;
    setRunning(false);
  }, [populating]);

  return { run, history, startedAtMs, running, start };
}
